import Guid from "_shared/guids/guid";
import EmailLogEntrySchema, {
  type EmailLogEntryJSON,
  type EmailLogEntrySummaryJSON,
  type EmailLogOutcome,
} from "users/super-users/emails/_schemas/email-log-entry-schema";

/** One row of the email log: who a letter was for, what it was, and what happened to it (#6922). */
export class EmailLogEntrySummary {
  public static create(params: EmailLogEntrySummaryJSON): EmailLogEntrySummary {
    return new EmailLogEntrySummary(EmailLogEntrySchema.validateSummary(params));
  }

  public static fromJSON(json: unknown): EmailLogEntrySummary | undefined {
    if (!json) return undefined;
    return EmailLogEntrySummary.create(json as EmailLogEntrySummaryJSON);
  }

  public readonly id: Guid;
  public readonly recordedAt: Date;
  public readonly environment: string;
  public readonly template?: string;
  public readonly recipient?: string;
  public readonly carbonCopy: string;
  public readonly subject?: string;
  public readonly outcome: EmailLogOutcome;

  /** Whether the letter was recorded instead of sent, which is all integration does. */
  public get isCaptured(): boolean {
    return this.outcome === "Captured";
  }

  protected constructor(json: EmailLogEntrySummaryJSON) {
    this.id = new Guid(json.id);
    this.recordedAt = new Date(json.recordedAt);
    this.environment = json.environment;
    this.template = json.template ?? undefined;
    this.recipient = json.recipient ?? undefined;
    this.carbonCopy = json.carbonCopy;
    this.subject = json.subject ?? undefined;
    this.outcome = json.outcome;
  }

  public toJSON(): EmailLogEntrySummaryJSON {
    return {
      id: this.id.value,
      recordedAt: this.recordedAt.toISOString(),
      environment: this.environment,
      template: this.template ?? null,
      recipient: this.recipient ?? null,
      carbonCopy: this.carbonCopy,
      subject: this.subject ?? null,
      outcome: this.outcome,
    };
  }

  public isEqualTo(other?: EmailLogEntrySummary): boolean {
    return Boolean(other) && JSON.stringify(this.toJSON()) === JSON.stringify(other?.toJSON());
  }

  public toString(): string {
    return `${this.outcome} ${this.subject ?? "(no subject)"} to ${this.recipient ?? "(no recipient)"}`;
  }

  public valueOf(): string {
    return this.id.value;
  }
}

/** One entry of the email log in full: every header, the outcome, and the bodies while they are kept. */
export default class EmailLogEntry extends EmailLogEntrySummary {
  public static override create(params: EmailLogEntryJSON): EmailLogEntry {
    return new EmailLogEntry(EmailLogEntrySchema.validate(params));
  }

  public static override fromJSON(json: unknown): EmailLogEntry | undefined {
    if (!json) return undefined;
    return EmailLogEntry.create(json as EmailLogEntryJSON);
  }

  public readonly sender?: string;
  public readonly blindCarbonCopy: string;
  public readonly htmlBody?: string;
  public readonly textBody?: string;
  /** Whether sign-in and invitation tokens were masked before the bodies were stored. */
  public readonly linksMasked: boolean;
  /** When the retention sweep removed the bodies. */
  public readonly bodiesRemovedAt?: Date;
  public readonly providerMessageId?: string;
  public readonly error?: string;
  public readonly correlationKind?: string;
  public readonly correlationId?: Guid;

  protected constructor(json: EmailLogEntryJSON) {
    super(json);
    this.sender = json.sender ?? undefined;
    this.blindCarbonCopy = json.blindCarbonCopy;
    this.htmlBody = json.htmlBody ?? undefined;
    this.textBody = json.textBody ?? undefined;
    this.linksMasked = json.linksMasked;
    this.bodiesRemovedAt = json.bodiesRemovedAt ? new Date(json.bodiesRemovedAt) : undefined;
    this.providerMessageId = json.providerMessageId ?? undefined;
    this.error = json.error ?? undefined;
    this.correlationKind = json.correlationKind ?? undefined;
    this.correlationId = json.correlationId ? new Guid(json.correlationId) : undefined;
  }

  public override toJSON(): EmailLogEntryJSON {
    return {
      ...super.toJSON(),
      sender: this.sender ?? null,
      blindCarbonCopy: this.blindCarbonCopy,
      htmlBody: this.htmlBody ?? null,
      textBody: this.textBody ?? null,
      linksMasked: this.linksMasked,
      bodiesRemovedAt: this.bodiesRemovedAt?.toISOString() ?? null,
      providerMessageId: this.providerMessageId ?? null,
      error: this.error ?? null,
      correlationKind: this.correlationKind ?? null,
      correlationId: this.correlationId?.value ?? null,
    };
  }
}
